import { Link, useLocation, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useState } from "react";
import {
  LayoutDashboard, BookOpen, Upload, Image, Archive, Code,
  LogOut, Database, FolderOpen, EyeOff, KeyRound, Eye, EyeOff as EyeOffIcon, X, ShieldAlert, Users, GitBranch, Key, BarChart3, ShieldCheck
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { cn } from "../lib/utils";
import { toast } from "sonner";
import api from "../services/api";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "/questions", label: "Sorular", icon: BookOpen },
  { path: "/archives", label: "Arşivler", icon: FolderOpen },
  { path: "/import", label: "Toplu Import", icon: Upload },
  { path: "/media", label: "Medya Yöneticisi", icon: Image },
  { path: "/taxonomy", label: "Taksonomi", icon: GitBranch },
  { path: "/stats", label: "Soru Dağılımı", icon: BarChart3 },
  { path: "/quality", label: "Kalite Kontrolü", icon: ShieldCheck },
  { path: "/passive", label: "Pasif Sorular", icon: EyeOff },
  { path: "/admin-users", label: "Admin Kullanıcılar", icon: Users },
  { path: "/access", label: "Public API", icon: Code },
  { path: "/api-keys", label: "API Anahtarları", icon: Key },
];

function ChangePasswordModal({ onClose }) {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (next.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalıdır");
    if (next !== confirm) return toast.error("Yeni şifreler eşleşmiyor");
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: current,
        new_password: next
      });
      toast.success("Şifre başarıyla değiştirildi");
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Şifre değiştirilemedi");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm" data-testid="change-password-modal">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm mx-4 p-6">
        <div className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <KeyRound size={15} className="text-blue-600" />
            </div>
            <h2 className="text-base font-semibold text-slate-900">Şifre Değiştir</h2>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 transition-colors">
            <X size={16} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Mevcut Şifre</label>
            <div className="relative">
              <Input
                type={showCurrent ? "text" : "password"}
                value={current}
                onChange={(e) => setCurrent(e.target.value)}
                placeholder="Mevcut şifrenizi girin"
                data-testid="current-password-input"
                required
                className="h-9 text-sm pr-9 border-slate-200"
              />
              <button type="button" onClick={() => setShowCurrent(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showCurrent ? <EyeOffIcon size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Yeni Şifre</label>
            <div className="relative">
              <Input
                type={showNext ? "text" : "password"}
                value={next}
                onChange={(e) => setNext(e.target.value)}
                placeholder="En az 6 karakter"
                data-testid="new-password-input"
                required
                className="h-9 text-sm pr-9 border-slate-200"
              />
              <button type="button" onClick={() => setShowNext(v => !v)}
                className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600">
                {showNext ? <EyeOffIcon size={14} /> : <Eye size={14} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Yeni Şifre (Tekrar)</label>
            <Input
              type="password"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              placeholder="Yeni şifreyi tekrar girin"
              data-testid="confirm-password-input"
              required
              className="h-9 text-sm border-slate-200"
            />
            {confirm && next && confirm !== next && (
              <p className="text-xs text-red-500 mt-1">Şifreler eşleşmiyor</p>
            )}
          </div>

          <div className="flex gap-2 pt-1">
            <Button type="submit" disabled={saving} data-testid="save-password-btn"
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white h-9 text-sm">
              {saving ? "Kaydediliyor..." : "Şifreyi Kaydet"}
            </Button>
            <Button type="button" variant="outline" onClick={onClose}
              className="h-9 text-sm border-slate-200">
              İptal
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}


export default function Layout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [showChangePassword, setShowChangePassword] = useState(false);

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <div className="flex h-screen bg-white overflow-hidden">
      <aside className="w-56 bg-slate-50 border-r border-slate-200 flex flex-col shrink-0">
        {/* Logo */}
        <div className="px-4 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-7 h-7 bg-blue-600 rounded-md flex items-center justify-center">
              <Database size={14} className="text-white" strokeWidth={2} />
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 leading-tight">
                Soru Bankası
              </p>
              <p className="text-xs text-slate-400 leading-tight">v1.1 Admin Panel</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-0.5 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive =
              location.pathname === item.path ||
              (item.path === "/questions" && location.pathname.startsWith("/questions"));
            return (
              <Link
                key={item.path}
                to={item.path}
                data-testid={`nav-${item.path.replace("/", "")}`}
                className={cn(
                  "flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-all duration-150",
                  isActive
                    ? "bg-white text-blue-600 font-medium shadow-sm border border-slate-200"
                    : "text-slate-500 hover:bg-slate-100 hover:text-slate-900"
                )}
              >
                <Icon size={15} strokeWidth={isActive ? 2 : 1.5} />
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="p-2 border-t border-slate-200">
          <div className="px-3 py-2 mb-0.5">
            <p className="text-xs font-semibold text-slate-800 truncate">{user?.name || "Admin"}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowChangePassword(true)}
            data-testid="change-password-btn"
            className="w-full justify-start gap-2 text-slate-400 hover:text-blue-600 hover:bg-blue-50 text-xs h-8"
          >
            <KeyRound size={13} />
            Şifre Değiştir
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-btn"
            className="w-full justify-start gap-2 text-slate-400 hover:text-red-500 hover:bg-red-50 text-xs h-8"
          >
            <LogOut size={13} />
            Çıkış Yap
          </Button>
        </div>
      </aside>

      <main className="flex-1 overflow-auto bg-white">
        <Outlet />
      </main>

      {showChangePassword && (
        <ChangePasswordModal onClose={() => setShowChangePassword(false)} />
      )}
    </div>
  );
}
